import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Truck, FileText, ReceiptText, Send, LogOut, Menu, User, HelpCircle, Phone, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/collections", label: "Collections", icon: Truck, testId: "nav-collections" },
  { to: "/documents", label: "Documents", icon: FileText, testId: "nav-documents" },
  { to: "/invoices", label: "Invoices", icon: ReceiptText, testId: "nav-invoices" },
  { to: "/requests", label: "Requests", icon: Send, testId: "nav-requests" },
];

const NavItems = ({ onNavigate }) => (
  <nav className="flex flex-col gap-1">
    {NAV.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        data-testid={item.testId}
        onClick={onNavigate}
        className={({ isActive }) =>
          `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
            isActive
              ? "bg-[var(--hh-amber-50)] text-[color:var(--hh-ink)]"
              : "text-[color:var(--hh-slate)] hover:bg-[var(--hh-band)]"
          }`
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-[var(--hh-amber)]" />
            )}
            <item.icon className={`h-[18px] w-[18px] ${isActive ? "text-[color:var(--hh-amber-600)]" : ""}`} />
            {item.label}
          </>
        )}
      </NavLink>
    ))}
  </nav>
);

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentTitle = NAV.find((n) => location.pathname.startsWith(n.to))?.label || "Portal";

  const initials = (user?.full_name || user?.email || "?")
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[var(--hh-band)]">
      {/* Sidebar (desktop) */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[264px] flex-col border-r border-[color:var(--hh-line)] bg-white lg:flex">
        <div className="flex h-16 items-center border-b border-[color:var(--hh-line)] px-5">
          <Logo />
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-5">
          <NavItems />
        </div>
        <div className="border-t border-[color:var(--hh-line)] p-4">
          <div className="rounded-xl bg-[var(--hh-band)] p-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-[color:var(--hh-slate)]">
              <Phone className="h-3.5 w-3.5 text-[color:var(--hh-amber-600)]" /> Need help?
            </div>
            <a href="tel:+447578850395" className="mt-1 block text-sm font-medium text-[color:var(--hh-ink)] hover:text-[color:var(--hh-amber-600)]">
              07578 850395
            </a>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="lg:pl-[264px]">
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-[color:var(--hh-line)] bg-white/95 px-4 backdrop-blur sm:px-6">
          <div className="flex items-center gap-3">
            {/* Mobile menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0">
                <div className="flex h-16 items-center border-b border-[color:var(--hh-line)] px-5">
                  <Logo />
                </div>
                <div className="px-3 py-5">
                  <NavItems onNavigate={() => setMobileOpen(false)} />
                </div>
              </SheetContent>
            </Sheet>
            <span className="font-display text-lg font-semibold text-[color:var(--hh-ink)]">{currentTitle}</span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-button" className="hh-focus flex items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-[var(--hh-band)]">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-[var(--hh-amber-50)] text-xs font-semibold text-[color:var(--hh-amber-600)]">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden max-w-[160px] truncate text-sm font-medium text-[color:var(--hh-ink)] sm:block">
                  {user?.full_name || user?.email}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account">
                <KeyRound className="mr-2 h-4 w-4" /> Change password
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/requests")} data-testid="menu-help">
                <HelpCircle className="mr-2 h-4 w-4" /> Get support
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="text-rose-600 focus:text-rose-600">
                <LogOut className="mr-2 h-4 w-4" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <div className="hh-accent-strip" />

        <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">{children}</main>
      </div>
    </div>
  );
};

export default AppShell;
